// landIeul の e-Stat 連携 (utils/eStatApi/geteStatData.ts, utils/vacantHouseRate/*,
// utils/populationChange/*, utils/rentalHousingConstructionCost/*,
// hooks/eStat/useNumberOfRentalPropertiesConstructed.ts) の忠実移植。
// API URL / appId / パラメータは landIeul と完全に同一。

export interface EStatRow {
  $: string;
  "@area": string;
  "@cat01": string;
  "@cat02": string;
  "@tab": string;
  "@time": string;
  "@unit": string;
}

const APP_ID = "2f8c710b34fddbd4f32f430b80f41603d8361533";

export async function geteStatData(url: string): Promise<EStatRow[] | undefined> {
  try {
    const res = await fetch(url);
    if (!res.ok) {
      // eslint-disable-next-line no-console
      console.log(res.status, res.statusText);
      return undefined;
    }
    const data = await res.json();
    return data.GET_STATS_DATA.STATISTICAL_DATA.DATA_INF.VALUE as EStatRow[];
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log("Error", (e as Error).message);
    return undefined;
  }
}

const year = (row: EStatRow): number => Number(row["@time"].slice(0, 4));
const value = (row: EStatRow): number => Number(row.$);

/* ------------------------------------------------------------------ *
 * 空き家率推移
 * ------------------------------------------------------------------ */

export interface VacantHouseRatePoint {
  vacantHouseRate: number;
  year: number;
}

export async function fetchVacantHouseRate(
  areaCode: string,
): Promise<{ graphData: VacantHouseRatePoint[]; isIncrease: boolean } | undefined> {
  const urls = [
    // 2018（平成30）年の空き家数
    `//api.e-stat.go.jp/rest/3.0/app/json/getStatsData?cdArea=${areaCode}&cdCat01=1%2C22&appId=${APP_ID}&lang=J&statsDataId=0003355276&metaGetFlg=Y&cntGetFlg=N&explanationGetFlg=Y&annotationGetFlg=Y&sectionHeaderFlg=1&replaceSpChars=0`,
    // 2013（平成25）年の空き家数
    `//api.e-stat.go.jp/rest/3.0/app/json/getStatsData?cdArea=${areaCode}&cdCat01=00001%2C00008&appId=${APP_ID}&lang=J&statsDataId=0003099761&metaGetFlg=Y&cntGetFlg=N&explanationGetFlg=Y&annotationGetFlg=Y&sectionHeaderFlg=1&replaceSpChars=0`,
    // 2008（平成20）年の空き家数
    `//api.e-stat.go.jp/rest/3.0/app/json/getStatsData?cdArea=${areaCode}&cdCat01=01%2C06&cdCat02=00&appId=${APP_ID}&lang=J&statsDataId=0003009759&metaGetFlg=Y&cntGetFlg=N&explanationGetFlg=Y&annotationGetFlg=Y&sectionHeaderFlg=1&replaceSpChars=0`,
    // 2003（平成15）年の空き家数
    `//api.e-stat.go.jp/rest/3.0/app/json/getStatsData?cdArea=${areaCode}&cdCat02=001%2C006&cdCat01=00700&appId=${APP_ID}&lang=J&statsDataId=0000083298&metaGetFlg=Y&cntGetFlg=N&explanationGetFlg=Y&annotationGetFlg=Y&sectionHeaderFlg=1&replaceSpChars=0`,
    // 1998（平成10）年の空き家数
    `//api.e-stat.go.jp/rest/3.0/app/json/getStatsData?cdArea=${areaCode}&cdCat02=002%2C007&cdCat01=00700&appId=${APP_ID}&lang=J&statsDataId=0000081625&metaGetFlg=Y&cntGetFlg=N&explanationGetFlg=Y&annotationGetFlg=Y&sectionHeaderFlg=1&replaceSpChars=0`,
  ];
  const results: Array<EStatRow[] | undefined> = [];
  for (const url of urls) {
    // eslint-disable-next-line no-await-in-loop
    results.push(await geteStatData(url));
  }
  if (results.some((r) => !r)) return undefined;
  const rows = results as EStatRow[][];

  // 0 番目に住宅数、1 番目に空き家数が入っている
  const houses = rows
    .map((r) => ({ house: value(r[0]), year: year(r[0]) }))
    .reverse();
  const vacantHouses = rows
    .map((r) => ({ vacantHouse: value(r[1]), year: year(r[1]) }))
    .reverse();

  const graphData: VacantHouseRatePoint[] = [];
  for (let i = 0; i < houses.length; i += 1) {
    graphData.push({
      vacantHouseRate: Math.floor(
        (vacantHouses[i].vacantHouse /
          (houses[i].house + vacantHouses[i].vacantHouse)) *
          100,
      ),
      year: vacantHouses[i].year,
    });
  }
  const at2018 = graphData.filter((e) => e.year === 2018);
  const at2013 = graphData.filter((e) => e.year === 2013);
  const isIncrease = at2018[0].vacantHouseRate > at2013[0].vacantHouseRate;
  return { graphData, isIncrease };
}

/* ------------------------------------------------------------------ *
 * 人口推移
 * ------------------------------------------------------------------ */

export interface PopulationPoint {
  population: number;
  year: number;
}

export async function fetchPastPopulationChanges(
  areaCode: string,
): Promise<PopulationPoint[] | undefined> {
  const url = `//api.e-stat.go.jp/rest/3.0/app/json/getStatsData?cdArea=${areaCode}&cdTime=2005000001%2C2010000001%2C2015000001%2C2020000000&cdCat01=000&cdCat02=001&appId=${APP_ID}&lang=J&statsDataId=0003448232&metaGetFlg=Y&cntGetFlg=N&explanationGetFlg=Y&annotationGetFlg=Y&sectionHeaderFlg=1&replaceSpChars=0`;
  const rows = await geteStatData(url);
  if (!rows) return undefined;
  return rows.map((row) => ({ population: value(row), year: year(row) }));
}

export function isIncreasePopulation(points: PopulationPoint[]): boolean {
  const at2020 = points.filter((e) => e.year === 2020);
  const at2015 = points.filter((e) => e.year === 2015);
  return (at2020[0]?.population as number) > (at2015[0]?.population as number);
}

/* ------------------------------------------------------------------ *
 * 新規貸家着工数
 * ------------------------------------------------------------------ */

export interface NewRentalHousePoint {
  rentalHouse: number;
  year: number;
}

export async function fetchNumberOfNewRentalHouse(
  areaCode: string,
): Promise<{ graphData: NewRentalHousePoint[]; isIncrease: boolean } | undefined> {
  const url = `//api.e-stat.go.jp/rest/3.0/app/json/getStatsData?cdArea=${areaCode}&cdTab=18&cdCat01=12&cdCat02=13&cdTime=2021000000%2C2016000000%2C2011000000&appId=${APP_ID}&lang=J&statsDataId=0003114541&metaGetFlg=Y&cntGetFlg=N&explanationGetFlg=Y&annotationGetFlg=Y&sectionHeaderFlg=1&replaceSpChars=0`;
  const rows = await geteStatData(url);
  if (!rows) return undefined;
  const graphData = rows
    .map((row) => ({ rentalHouse: value(row), year: year(row) }))
    .reverse();
  const at2021 = graphData.filter((e) => e.year === 2021);
  const at2016 = graphData.filter((e) => e.year === 2016);
  return {
    graphData,
    isIncrease: at2021[0].rentalHouse > at2016[0].rentalHouse,
  };
}

/* ------------------------------------------------------------------ *
 * 賃貸物件建築数の推移（構造別）
 * ------------------------------------------------------------------ */

export interface RentalPropertiesConstructedPoint {
  totalConstructed: number;
  totalWooden: number;
  totalNotWooden: number;
  year: number;
}

export async function fetchNumberOfRentalPropertiesConstructed(areaCode: string): Promise<
  | {
      graphData: RentalPropertiesConstructedPoint[];
      isIncreaseTotal: boolean;
      isIncreaseTotalWooden: boolean;
      isIncreaseTotalNotWooden: boolean;
    }
  | undefined
> {
  const url = `//api.e-stat.go.jp/rest/3.0/app/json/getStatsData?cdCat03=13&cdArea=${areaCode}&cdTab=19&cdCat01=11&cdCat02=11%2C12%2C13%2C14%2C15&cdTime=2021100000%2C2016100000%2C2011100000%2C2006100000%2C2001100000%2C1996100000%2C1991100000&appId=${APP_ID}&lang=J&statsDataId=0003119736&metaGetFlg=Y&cntGetFlg=N&explanationGetFlg=Y&annotationGetFlg=Y&sectionHeaderFlg=1&replaceSpChars=0`;
  const rows = await geteStatData(url);
  if (!rows) return undefined;
  const byCat = (cat: string): EStatRow[] => rows.filter((r) => r["@cat02"] === cat);

  const total = byCat("11");
  const wooden = byCat("12");
  const steelReinforcedConcrete = byCat("13");
  const reinforcedConcrete = byCat("14");
  const steel = byCat("15");

  const graphData: RentalPropertiesConstructedPoint[] = [];
  for (let i = 0; i < total.length; i += 1) {
    graphData.unshift({
      totalConstructed: value(total[i]),
      totalWooden: value(wooden[i]),
      totalNotWooden:
        value(steelReinforcedConcrete[i]) +
        value(reinforcedConcrete[i]) +
        value(steel[i]),
      year: year(total[i]),
    });
  }
  const at2021 = graphData.filter((e) => e.year === 2021);
  const at2016 = graphData.filter((e) => e.year === 2016);
  return {
    graphData,
    // NOTE: totalConstructed ではなく木造 + 非木造の和で比較する (useNumberOfNewRentalHouses と同じ)
    isIncreaseTotal:
      at2021[0].totalWooden + at2021[0].totalNotWooden >
      at2016[0].totalWooden + at2016[0].totalNotWooden,
    isIncreaseTotalWooden: at2021[0].totalWooden > at2016[0].totalWooden,
    isIncreaseTotalNotWooden: at2021[0].totalNotWooden > at2016[0].totalNotWooden,
  };
}

/* ------------------------------------------------------------------ *
 * 賃貸物件の築年数状況
 * ------------------------------------------------------------------ */

export interface RentalPropertyAgePoint {
  totalRentalHouse: number;
  woodenRentalHouse: number;
  notWoodenRentalHouse: number;
  age: string;
}

export async function fetchRentalPropertyAgeStatus(areaCode: string): Promise<
  | {
      graphData: RentalPropertyAgePoint[];
      modeAgeInTotal: string;
      modeAgeInWooden: string;
      modeAgeInNotWooden: string;
    }
  | undefined
> {
  const url = `//api.e-stat.go.jp/rest/3.0/app/json/getStatsData?cdCat01=01%2C02%2C04%2C05%2C06%2C07%2C08%2C09%2C10%2C11&cdArea=${areaCode}&cdCat02=13%2C131%2C132&appId=${APP_ID}&lang=J&statsDataId=0003355295&metaGetFlg=Y&cntGetFlg=N&explanationGetFlg=Y&annotationGetFlg=Y&sectionHeaderFlg=1&replaceSpChars=0`;
  const rows = await geteStatData(url);
  if (!rows) return undefined;
  const byCat01 = (cat: string): EStatRow[] => rows.filter((r) => r["@cat01"] === cat);

  // 0 番目が民営借家、1 番目が民営借家（木造）、2 番目が民営借家（非木造）
  const before1950 = byCat01("01");
  const y1951to1970 = byCat01("02");
  const groups: Array<{ rows: EStatRow[]; age: string }> = [
    { rows: [], age: "53年以上" },
    { rows: byCat01("04"), age: "43~52年" },
    { rows: byCat01("05"), age: "33~42年" },
    { rows: byCat01("06"), age: "28~32年" },
    { rows: byCat01("07"), age: "23~27年" },
    { rows: byCat01("08"), age: "18~22年" },
    { rows: byCat01("09"), age: "13~17年" },
    { rows: byCat01("10"), age: "8~12年" },
    { rows: byCat01("11"), age: "7年以内" },
  ];

  const graphData: RentalPropertyAgePoint[] = groups.map((group, index) => {
    if (index === 0) {
      return {
        totalRentalHouse: value(before1950[0]) + value(y1951to1970[0]),
        woodenRentalHouse: value(before1950[1]) + value(y1951to1970[1]),
        notWoodenRentalHouse: value(before1950[2]) + value(y1951to1970[2]),
        age: group.age,
      };
    }
    return {
      totalRentalHouse: value(group.rows[0]),
      woodenRentalHouse: value(group.rows[1]),
      notWoodenRentalHouse: value(group.rows[2]),
      age: group.age,
    };
  });

  const modeAge = (key: keyof RentalPropertyAgePoint): string => {
    const max = Math.max(...graphData.map((e) => e[key] as number));
    const hit = graphData.find((e) => e[key] === max);
    return hit ? `築${hit.age}` : "";
  };

  return {
    graphData,
    modeAgeInTotal: modeAge("totalRentalHouse"),
    modeAgeInWooden: modeAge("woodenRentalHouse"),
    modeAgeInNotWooden: modeAge("notWoodenRentalHouse"),
  };
}

/* ------------------------------------------------------------------ *
 * 1 戸当たり建築工事費
 * ------------------------------------------------------------------ */

export interface ConstructionCostPoint {
  woodenConstructionCost: number;
  steelReinforcedConstructionCost: number;
  reinforcedConcreteConstructionCost: number;
  steelConstructionCost: number;
  year: number;
}

export async function fetchConstructionCosts(
  areaCode: string,
): Promise<{ graphData: ConstructionCostPoint[]; isUp: boolean } | undefined> {
  const url = `//api.e-stat.go.jp/rest/3.0/app/json/getStatsData?cdCat03=11&cdArea=${areaCode}&cdTab=20&cdCat01=14&cdCat02=12%2C13%2C14%2C15&cdTime=2021100000%2C2020100000%2C2019100000%2C2018100000%2C2017100000&appId=${APP_ID}&lang=J&statsDataId=0003114631&metaGetFlg=Y&cntGetFlg=N&explanationGetFlg=Y&annotationGetFlg=Y&sectionHeaderFlg=1&replaceSpChars=0`;
  const rows = await geteStatData(url);
  if (!rows) return undefined;
  const byCat = (cat: string): EStatRow[] => rows.filter((r) => r["@cat02"] === cat);
  const wooden = byCat("12");
  const steelReinforcedConcrete = byCat("13");
  const reinforcedConcrete = byCat("14");
  const steel = byCat("15");

  const graphData: ConstructionCostPoint[] = [];
  for (let i = 0; i < wooden.length; i += 1) {
    graphData.unshift({
      woodenConstructionCost: value(wooden[i]),
      steelReinforcedConstructionCost: value(steelReinforcedConcrete[i]),
      reinforcedConcreteConstructionCost: value(reinforcedConcrete[i]),
      steelConstructionCost: value(steel[i]),
      year: year(wooden[i]),
    });
  }
  const at2021 = wooden.filter((r) => year(r) === 2021);
  const at2020 = wooden.filter((r) => year(r) === 2020);
  return { graphData, isUp: value(at2021[0]) > value(at2020[0]) };
}
